import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken, logoutSession } from '../utils/authSession';
import { apiGet, apiPost, apiDelete } from '../api/client';

const SEVERITY_BADGE = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'secondary'
};

const EVENT_TYPES = [
  'login_failed',
  'login_locked',
  'path_probe',
  'sqli_probe',
  'xss_probe',
  'path_traversal',
  'rate_limited',
  'auth_rate_limited',
  'write_rate_limited',
  'cors_blocked',
  'redirect_attack',
  'unauthorized_file_access',
  'blocked_ip_request',
  'ip_auto_blocked'
];

const BLOCK_DURATIONS = [
  { label: '1 hour', hours: 1 },
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 168 },
  { label: 'Permanent', hours: null }
];

function EventDetailsBody({ event }) {
  if (!event) return null;

  const lines = event.details_lines || [];
  return (
    <div>
      <dl className="row mb-0">
        <dt className="col-sm-4">Event</dt>
        <dd className="col-sm-8">{event.event_label || event.event_type}</dd>

        <dt className="col-sm-4">Severity</dt>
        <dd className="col-sm-8 text-capitalize">{event.severity}</dd>

        <dt className="col-sm-4">IP address</dt>
        <dd className="col-sm-8"><code>{event.client_ip}</code></dd>

        <dt className="col-sm-4">Time</dt>
        <dd className="col-sm-8">{new Date(event.created_at).toLocaleString()}</dd>

        <dt className="col-sm-4">Request</dt>
        <dd className="col-sm-8">
          <code>{event.method || '—'} {event.path || '—'}</code>
        </dd>

        {event.username && (
          <>
            <dt className="col-sm-4">User</dt>
            <dd className="col-sm-8">{event.username}</dd>
          </>
        )}

        {event.user_agent && (
          <>
            <dt className="col-sm-4">Browser / client</dt>
            <dd className="col-sm-8"><small>{event.user_agent}</small></dd>
          </>
        )}
      </dl>

      <hr />

      <h6 className="mb-3">What happened</h6>
      <p className="mb-3">{event.details_summary || 'No extra details recorded.'}</p>

      {lines.length > 0 && (
        <>
          <h6 className="mb-3">Full details</h6>
          <dl className="row mb-0">
            {lines.map((line) => (
              <div className="col-12 mb-2" key={`${line.label}-${line.value}`}>
                <dt className="text-muted small mb-1">{line.label}</dt>
                <dd className="mb-0">
                  <code className="text-wrap d-inline-block" style={{ whiteSpace: 'pre-wrap' }}>
                    {line.value}
                  </code>
                </dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </div>
  );
}

function AdminSecurityDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('events');
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [blockedIps, setBlockedIps] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    client_ip: '',
    event_type: '',
    severity: ''
  });
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [traceData, setTraceData] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [blockModal, setBlockModal] = useState({ ip: '', hours: 24, reason: '' });

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${getAccessToken()}` }
  }), []);

  const handleAuthError = useCallback(async (error) => {
    if (error.response?.status === 401) {
      await logoutSession({ redirect: false });
      navigate('/admin/login');
    }
  }, [navigate]);

  const fetchSummary = useCallback(async () => {
    const data = await apiGet('/api/admin/security/summary', authHeaders());
    setSummary(data);
  }, [authHeaders]);

  const fetchEvents = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.client_ip) params.append('client_ip', filters.client_ip);
    if (filters.event_type) params.append('event_type', filters.event_type);
    if (filters.severity) params.append('severity', filters.severity);
    params.append('page', String(currentPage));

    const data = await apiGet(`/api/admin/security/events?${params}`, authHeaders());
    setEvents(data.events || []);
    setPagination(data.pagination || {});
  }, [authHeaders, filters, currentPage]);

  const fetchLoginAttempts = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.client_ip) params.append('client_ip', filters.client_ip);
    params.append('page', String(currentPage));

    const data = await apiGet(`/api/admin/security/login-attempts?${params}`, authHeaders());
    setLoginAttempts(data.attempts || []);
    setPagination(data.pagination || {});
  }, [authHeaders, filters.client_ip, currentPage]);

  const fetchBlockedIps = useCallback(async () => {
    const data = await apiGet('/api/admin/security/blocked-ips', authHeaders());
    setBlockedIps(data.blockedIps || []);
  }, [authHeaders]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setActionError('');
      await fetchSummary();

      if (activeTab === 'events') {
        await fetchEvents();
      } else if (activeTab === 'login-attempts') {
        await fetchLoginAttempts();
      } else {
        await fetchBlockedIps();
      }
    } catch (error) {
      await handleAuthError(error);
      console.error('Error loading security dashboard:', error);
      setActionError('Failed to load security data. Run npm run create-security-monitoring-tables in server/.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, fetchSummary, fetchEvents, fetchLoginAttempts, fetchBlockedIps, handleAuthError]);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate, loadData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.client_ip, filters.event_type, filters.severity, activeTab]);

  const openBlockModal = (ip, reason = '') => {
    setBlockModal({
      ip,
      hours: 24,
      reason: reason || `Blocked from security dashboard`
    });
  };

  const submitBlockIp = async () => {
    const { ip, hours, reason } = blockModal;
    if (!ip) return;

    try {
      setActionError('');
      if (hours == null) {
        await apiPost(
          '/api/admin/security/block-ip',
          { ip, reason },
          authHeaders()
        );
      } else {
        await apiPost(
          '/api/admin/security/block-ip-from-request',
          {
            client_ip: ip,
            duration_hours: hours,
            reason
          },
          authHeaders()
        );
      }
      const durationLabel = hours ? `${hours} hour(s)` : 'permanently';
      setActionMessage(`Blocked ${ip} ${durationLabel}.`);
      setBlockModal({ ip: '', hours: 24, reason: '' });
      if (traceData?.ip === ip) {
        await traceIp(ip);
      }
      await loadData();
    } catch (error) {
      await handleAuthError(error);
      setActionError(error.response?.data?.error || 'Failed to block IP.');
    }
  };

  const unblockIp = async (ip) => {
    if (!window.confirm(`Unblock IP ${ip}?`)) return;

    try {
      setActionError('');
      await apiDelete(`/api/admin/security/block-ip/${encodeURIComponent(ip)}`, authHeaders());
      setActionMessage(`Unblocked ${ip}.`);
      if (traceData?.ip === ip) {
        await traceIp(ip);
      }
      await loadData();
    } catch (error) {
      await handleAuthError(error);
      setActionError(error.response?.data?.error || 'Failed to unblock IP.');
    }
  };

  const traceIp = async (ip) => {
    try {
      setTraceLoading(true);
      setActionError('');
      const data = await apiGet(`/api/admin/security/ip/${encodeURIComponent(ip)}/trace`, authHeaders());
      setTraceData(data);
      setFilters((prev) => ({ ...prev, client_ip: ip }));
    } catch (error) {
      await handleAuthError(error);
      setActionError(error.response?.data?.error || 'Failed to trace IP.');
    } finally {
      setTraceLoading(false);
    }
  };

  if (loading && !summary) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const stats = summary?.last24Hours || {};

  return (
    <AdminLayout>
      <div className="container-fluid py-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="h3 mb-1">Security Monitoring</h1>
            <p className="text-muted mb-0">Trace suspicious IPs, review full event details, and block threats.</p>
          </div>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={loadData} disabled={loading}>
            <i className="bi bi-arrow-clockwise me-1" />
            Refresh
          </button>
        </div>

        {actionMessage && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            {actionMessage}
            <button type="button" className="btn-close" onClick={() => setActionMessage('')} aria-label="Close" />
          </div>
        )}
        {actionError && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {actionError}
            <button type="button" className="btn-close" onClick={() => setActionError('')} aria-label="Close" />
          </div>
        )}

        <div className="row g-3 mb-4">
          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">Events (24h)</div>
                <div className="h4 mb-0">{stats.total_events || 0}</div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">Critical</div>
                <div className="h4 mb-0 text-danger">{stats.critical_count || 0}</div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">High</div>
                <div className="h4 mb-0 text-warning">{stats.high_count || 0}</div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">Blocked IPs</div>
                <div className="h4 mb-0">{stats.blockedIps || 0}</div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">Login records</div>
                <div className="h4 mb-0">{stats.loginAttemptRecords || 0}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-lg-6">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">Top IPs (24h)</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>IP</th>
                        <th>Events</th>
                        <th>Threat score</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {(summary?.topIps || []).length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center text-muted py-3">No events yet</td>
                        </tr>
                      ) : (
                        summary.topIps.map((row) => (
                          <tr key={row.client_ip}>
                            <td><code>{row.client_ip}</code></td>
                            <td>{row.event_count}</td>
                            <td>{row.threat_score}</td>
                            <td className="text-end">
                              <div className="btn-group btn-group-sm">
                                <button type="button" className="btn btn-outline-primary" onClick={() => traceIp(row.client_ip)}>
                                  Trace
                                </button>
                                <button type="button" className="btn btn-outline-danger" onClick={() => openBlockModal(row.client_ip)}>
                                  Block
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">Events by type (24h)</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(summary?.eventsByType || []).length === 0 ? (
                        <tr>
                          <td colSpan="2" className="text-center text-muted py-3">No events yet</td>
                        </tr>
                      ) : (
                        summary.eventsByType.map((row) => (
                          <tr key={row.event_type}>
                            <td>{row.event_type.replace(/_/g, ' ')}</td>
                            <td>{row.count}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button type="button" className={`nav-link ${activeTab === 'events' ? 'active' : ''}`} onClick={() => setActiveTab('events')}>
              Security events
            </button>
          </li>
          <li className="nav-item">
            <button type="button" className={`nav-link ${activeTab === 'login-attempts' ? 'active' : ''}`} onClick={() => setActiveTab('login-attempts')}>
              Failed logins
            </button>
          </li>
          <li className="nav-item">
            <button type="button" className={`nav-link ${activeTab === 'blocked' ? 'active' : ''}`} onClick={() => setActiveTab('blocked')}>
              Blocked IPs
            </button>
          </li>
        </ul>

        {activeTab !== 'blocked' && (
          <div className="row mb-3 g-2">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Filter by IP..."
                value={filters.client_ip}
                onChange={(e) => setFilters({ ...filters, client_ip: e.target.value })}
              />
            </div>
            {activeTab === 'events' && (
              <>
                <div className="col-md-4">
                  <select className="form-select" value={filters.event_type} onChange={(e) => setFilters({ ...filters, event_type: e.target.value })}>
                    <option value="">All event types</option>
                    {EVENT_TYPES.map((type) => (
                      <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <select className="form-select" value={filters.severity} onChange={(e) => setFilters({ ...filters, severity: e.target.value })}>
                    <option value="">All severities</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </>
            )}
          </div>
        )}

        <div className="card">
          <div className="card-body">
            {activeTab === 'events' && (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Severity</th>
                      <th>Event</th>
                      <th>IP</th>
                      <th>Request</th>
                      <th>Details</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {events.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center text-muted">No security events found</td>
                      </tr>
                    ) : (
                      events.map((event) => (
                        <tr key={event.id}>
                          <td className="text-nowrap">{new Date(event.created_at).toLocaleString()}</td>
                          <td>
                            <span className={`badge bg-${SEVERITY_BADGE[event.severity] || 'secondary'}`}>
                              {event.severity}
                            </span>
                          </td>
                          <td>{event.event_label || event.event_type}</td>
                          <td><code>{event.client_ip}</code></td>
                          <td className="text-truncate" style={{ maxWidth: 180 }} title={event.path || ''}>
                            <small>{event.method || '—'} {event.path || '—'}</small>
                          </td>
                          <td style={{ maxWidth: 280 }}>
                            <div className="small">{event.details_summary || '—'}</div>
                            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setSelectedEvent(event)}>
                              View full details
                            </button>
                          </td>
                          <td className="text-end text-nowrap">
                            <div className="btn-group btn-group-sm">
                              <button type="button" className="btn btn-outline-primary" onClick={() => traceIp(event.client_ip)}>
                                Trace
                              </button>
                              <button type="button" className="btn btn-outline-danger" onClick={() => openBlockModal(event.client_ip)}>
                                Block
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'login-attempts' && (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Last failed</th>
                      <th>IP</th>
                      <th>Login tried</th>
                      <th>Failed attempts</th>
                      <th>Locked until</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {loginAttempts.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center text-muted">No failed login records</td>
                      </tr>
                    ) : (
                      loginAttempts.map((row) => (
                        <tr key={`${row.client_ip}-${row.login_identifier}`}>
                          <td>{new Date(row.last_failed_at).toLocaleString()}</td>
                          <td><code>{row.client_ip}</code></td>
                          <td>{row.login_identifier}</td>
                          <td>{row.failed_attempts}</td>
                          <td>{row.lock_until ? new Date(row.lock_until).toLocaleString() : '—'}</td>
                          <td className="text-end">
                            <div className="btn-group btn-group-sm">
                              <button type="button" className="btn btn-outline-primary" onClick={() => traceIp(row.client_ip)}>
                                Trace
                              </button>
                              <button type="button" className="btn btn-outline-danger" onClick={() => openBlockModal(row.client_ip)}>
                                Block
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'blocked' && (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>IP</th>
                      <th>Reason</th>
                      <th>Blocked by</th>
                      <th>Expires</th>
                      <th>Created</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {blockedIps.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center text-muted">No blocked IPs</td>
                      </tr>
                    ) : (
                      blockedIps.map((row) => (
                        <tr key={row.ip}>
                          <td><code>{row.ip}</code></td>
                          <td>{row.reason}</td>
                          <td>{row.blocked_by || 'system'}</td>
                          <td>{row.expires_at ? new Date(row.expires_at).toLocaleString() : 'Permanent'}</td>
                          <td>{new Date(row.created_at).toLocaleString()}</td>
                          <td className="text-end">
                            <div className="btn-group btn-group-sm">
                              <button type="button" className="btn btn-outline-primary" onClick={() => traceIp(row.ip)}>
                                Trace
                              </button>
                              <button type="button" className="btn btn-outline-secondary" onClick={() => unblockIp(row.ip)}>
                                Unblock
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab !== 'blocked' && pagination.pages > 1 && (
              <div className="mt-3 d-flex justify-content-between align-items-center">
                <p className="text-muted mb-0">
                  Page {pagination.page} of {pagination.pages} (Total: {pagination.total})
                </p>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))} disabled={loading || (pagination.page || 1) <= 1}>
                    Previous
                  </button>
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setCurrentPage((prev) => prev + 1)} disabled={loading || (pagination.page || 1) >= (pagination.pages || 1)}>
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedEvent && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Security event details</h5>
                <button type="button" className="btn-close" onClick={() => setSelectedEvent(null)} />
              </div>
              <div className="modal-body">
                <EventDetailsBody event={selectedEvent} />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-primary" onClick={() => { traceIp(selectedEvent.client_ip); setSelectedEvent(null); }}>
                  Trace this IP
                </button>
                <button type="button" className="btn btn-outline-danger" onClick={() => { openBlockModal(selectedEvent.client_ip); setSelectedEvent(null); }}>
                  Block this IP
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setSelectedEvent(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {blockModal.ip && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Block IP address</h5>
                <button type="button" className="btn-close" onClick={() => setBlockModal({ ip: '', hours: 24, reason: '' })} />
              </div>
              <div className="modal-body">
                <p>Block <code>{blockModal.ip}</code> from accessing the site.</p>
                <div className="mb-3">
                  <label className="form-label">Duration</label>
                  <select
                    className="form-select"
                    value={blockModal.hours ?? 'permanent'}
                    onChange={(e) => {
                      const value = e.target.value;
                      setBlockModal({
                        ...blockModal,
                        hours: value === 'permanent' ? null : Number(value)
                      });
                    }}
                  >
                    {BLOCK_DURATIONS.map((option) => (
                      <option key={option.label} value={option.hours ?? 'permanent'}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-0">
                  <label className="form-label">Reason</label>
                  <input
                    type="text"
                    className="form-control"
                    value={blockModal.reason}
                    onChange={(e) => setBlockModal({ ...blockModal, reason: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-danger" onClick={submitBlockIp}>Block IP</button>
                <button type="button" className="btn btn-secondary" onClick={() => setBlockModal({ ip: '', hours: 24, reason: '' })}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(traceData || traceLoading) && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">IP trace: <code>{traceData?.ip || '...'}</code></h5>
                <button type="button" className="btn-close" onClick={() => setTraceData(null)} />
              </div>
              <div className="modal-body">
                {traceLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : traceData && (
                  <>
                    <div className="row g-3 mb-4">
                      <div className="col-md-3">
                        <div className="border rounded p-3 h-100">
                          <div className="text-muted small">Status</div>
                          <div className={`fw-semibold ${traceData.isBlocked ? 'text-danger' : 'text-success'}`}>
                            {traceData.isBlocked ? 'Blocked' : 'Not blocked'}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="border rounded p-3 h-100">
                          <div className="text-muted small">Total events</div>
                          <div className="fw-semibold">{traceData.stats?.total_events || 0}</div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="border rounded p-3 h-100">
                          <div className="text-muted small">Critical / high</div>
                          <div className="fw-semibold">
                            {traceData.stats?.critical_count || 0} / {traceData.stats?.high_count || 0}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="border rounded p-3 h-100">
                          <div className="text-muted small">First / last seen</div>
                          <div className="small">
                            {traceData.stats?.first_seen ? new Date(traceData.stats.first_seen).toLocaleString() : '—'}
                            <br />
                            {traceData.stats?.last_seen ? new Date(traceData.stats.last_seen).toLocaleString() : '—'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {traceData.eventsByType?.length > 0 && (
                      <>
                        <h6>Activity breakdown</h6>
                        <ul className="mb-4">
                          {traceData.eventsByType.map((row) => (
                            <li key={row.event_type}>
                              {row.event_label || row.event_type}: <strong>{row.count}</strong>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    {traceData.loginAttempts?.length > 0 && (
                      <>
                        <h6>Failed login records</h6>
                        <div className="table-responsive mb-4">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Login tried</th>
                                <th>Failed attempts</th>
                                <th>Last failed</th>
                                <th>Locked until</th>
                              </tr>
                            </thead>
                            <tbody>
                              {traceData.loginAttempts.map((row) => (
                                <tr key={`${row.client_ip}-${row.login_identifier}`}>
                                  <td>{row.login_identifier}</td>
                                  <td>{row.failed_attempts}</td>
                                  <td>{new Date(row.last_failed_at).toLocaleString()}</td>
                                  <td>{row.lock_until ? new Date(row.lock_until).toLocaleString() : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}

                    <h6>Recent events from this IP</h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Event</th>
                            <th>Severity</th>
                            <th>Request</th>
                            <th>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {traceData.events.map((event) => (
                            <tr key={event.id}>
                              <td className="text-nowrap">{new Date(event.created_at).toLocaleString()}</td>
                              <td>{event.event_label || event.event_type}</td>
                              <td className="text-capitalize">{event.severity}</td>
                              <td><small>{event.method || '—'} {event.path || '—'}</small></td>
                              <td>
                                <div className="small">{event.details_summary}</div>
                                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setSelectedEvent(event)}>
                                  Full details
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                {traceData && (
                  traceData.isBlocked ? (
                    <button type="button" className="btn btn-outline-secondary" onClick={() => unblockIp(traceData.ip)}>
                      Unblock IP
                    </button>
                  ) : (
                    <button type="button" className="btn btn-outline-danger" onClick={() => openBlockModal(traceData.ip)}>
                      Block IP
                    </button>
                  )
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setTraceData(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminSecurityDashboard;
